import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Home, BarChart3, MessageCircle, User, Sparkles, Plus, FolderOpen } from "lucide-react";

const PRO_LINKS = [
  { to: "/pro/home", icon: Home, label: "Accueil", testid: "nav-pro-home" },
  { to: "/pro/activity", icon: BarChart3, label: "Activité", testid: "nav-pro-activity" },
  { to: "/pro/messages", icon: MessageCircle, label: "Messages", testid: "nav-pro-messages" },
  { to: "/pro/profile", icon: User, label: "Profil", testid: "nav-pro-profile" },
  { to: "/pro/ai", icon: Sparkles, label: "IA Pro", testid: "nav-pro-ai", premium: true },
];
const PART_LINKS = [
  { to: "/p/home", icon: Home, label: "Accueil", testid: "nav-p-home" },
  { to: "/p/create", icon: Plus, label: "Créer", testid: "nav-p-create" },
  { to: "/p/requests", icon: FolderOpen, label: "Demandes", testid: "nav-p-requests" },
  { to: "/p/messages", icon: MessageCircle, label: "Messages", testid: "nav-p-messages" },
  { to: "/p/ai", icon: Sparkles, label: "IA", testid: "nav-p-ai" },
];

// Material Design 3 style ripple. Absolute-positioned expanding circle on pointer-down.
function useRipple() {
  const containerRef = useRef(null);
  const [ripples, setRipples] = useState([]);
  const addRipple = (e) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 1.6;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? rect.left + rect.width / 2;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? rect.top + rect.height / 2;
    const x = clientX - rect.left - size / 2;
    const y = clientY - rect.top - size / 2;
    const id = Date.now() + Math.random();
    setRipples((r) => [...r, { id, x, y, size }]);
    setTimeout(() => setRipples((r) => r.filter((rp) => rp.id !== id)), 600);
  };
  return { containerRef, ripples, addRipple };
}

function NavItem({ link, activeColor }) {
  const { containerRef, ripples, addRipple } = useRipple();
  return (
    <NavLink
      to={link.to}
      data-testid={link.testid}
      onPointerDown={addRipple}
      ref={containerRef}
      className={({ isActive }) =>
        `relative flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl overflow-hidden select-none tap-scale ${
          isActive ? activeColor : "text-slate-400"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active pill background (MD3-style indicator) */}
          <span
            aria-hidden
            className={`absolute inset-x-3 top-1.5 h-8 rounded-full transition-all duration-300 ${
              isActive ? "bg-current opacity-10 scale-100" : "opacity-0 scale-75"
            }`}
          />
          <link.icon
            className={`relative w-5 h-5 transition-transform duration-200 ${isActive ? "scale-110" : ""}`}
            strokeWidth={isActive ? 2.4 : 2}
          />
          <span className={`relative text-[10px] tracking-wide ${isActive ? "font-semibold" : "font-medium"}`}>
            {link.label}
          </span>
          {link.premium && (
            <span
              aria-hidden
              className="absolute top-1 right-3 inline-block w-1.5 h-1.5 rounded-full bg-brand shadow-[0_0_0_2px_white]"
            />
          )}
          {/* Ripples */}
          {ripples.map((r) => (
            <span
              key={r.id}
              className="pointer-events-none absolute rounded-full bg-current opacity-25 animate-ripple"
              style={{ left: r.x, top: r.y, width: r.size, height: r.size }}
            />
          ))}
        </>
      )}
    </NavLink>
  );
}

export default function BottomNav({ role }) {
  const links = role === "pro" ? PRO_LINKS : PART_LINKS;
  const activeColor = role === "pro" ? "text-pro" : "text-brand";
  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-white/95 backdrop-blur-lg border-t border-border z-50"
      style={{
        boxShadow: "0 -6px 28px rgba(15,30,61,0.10), 0 -1px 0 rgba(15,30,61,0.04) inset",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="bottom-nav"
    >
      <div className="grid grid-cols-5 gap-1 px-2 py-1.5">
        {links.map((link) => (
          <NavItem key={link.to} link={link} activeColor={activeColor} />
        ))}
      </div>
    </nav>
  );
}
